import { HttpStatusCode } from "axios";
import { EPath } from "constants/path";
import { SecureStorageKeys } from "constants/secureStorage";
import {
  ErrorResponse,
  ErrorResponseFromJSON,
  Middleware,
  ResponseError,
} from "libs/cellera-sdk";

import { secureStorage } from "./secureStorage";

export const extractErrorMessage = async (
  response: Response,
): Promise<string> => {
  try {
    const responseText = await response.clone().text();
    const errorResponse: ErrorResponse = ErrorResponseFromJSON(
      JSON.parse(responseText),
    );

    // Return the most specific error message available
    if (errorResponse?.messages && errorResponse?.messages?.length > 0) {
      return errorResponse?.messages?.join(", ");
    }
    if (errorResponse?.message) {
      return errorResponse.message;
    }
    if (errorResponse?.errorCode) {
      return `Error ${errorResponse?.errorCode}`;
    }
  } catch (parseError) {
    console.warn("Could not parse error response:", parseError);
  }

  return "An unknown error occurred";
};

/**
 * Utility function to handle token expiration and redirect to login
 * This function can be called from anywhere in the app when token expiration is detected
 */
export const handleTokenExpiration = () => {
  console.warn(
    "Token expired or unauthorized access detected. Redirecting to login...",
  );

  // Clear the stored token
  secureStorage.remove(SecureStorageKeys.CUSTOM_TOKEN);

  // Redirect to login page
  // Use window.location.href to ensure a full page reload and clear any cached state
  window.location.href = EPath.LOGIN;
};

const isAuthError = (response: Response): boolean => {
  return (
    response?.status === HttpStatusCode.Unauthorized ||
    response?.status === HttpStatusCode.Forbidden
  );
};

/**
 * Middleware to handle token expiration and redirect to login
 * This middleware intercepts API responses and checks for 401/403 errors
 * When token expires, it clears the stored token and redirects to login
 */
export const apiMiddleWare: Middleware = {
  post: async (context) => {
    const { response } = context;
    if (isAuthError(response)) {
      handleTokenExpiration();
      return;
    }
    if (response?.ok === false) {
      const errorMessage = await extractErrorMessage(response);
      console.error("Error Message:", errorMessage);
      throw new ResponseError(response, errorMessage);
    }

    return response;
  },
};
